import React from 'react';
import type { ComponentProps, ReactNode } from 'react';
import { Pressable } from 'react-native-gesture-handler';

import CheckmarkSmall from '@/assets/icons/checkmark-small';
import { LottieLoader } from '@/components/LottieLoader';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { common } from '@/styles/styles';
import type { CashOutPhase } from '@/utils/entry-card/entry-cash-out-reducer';

import { ds } from '../lib/design.system';
import { CASH_OUT_PILL, EntryCardCashOutButton } from './EntryCardCashOutButton';
import type { CashOutControlsVariant } from './EntryCardCashOutButton';

/** The shared rounded button surface used by every non-idle phase. */
const CashOutPill = ({ children, ...boxProps }: ComponentProps<typeof Box>) => (
    <Box alignItems="center" justifyContent="center" {...boxProps}>
        {children}
    </Box>
);

/** Per-phase copy for the cash-out controls. */
export type CashOutLabels = {
    /** Idle CTA, e.g. "Cash out" (rendered as "Cash out • {icon} {amount}"). */
    offer: string;
    confirm: string;
    cancel: string;
    confirmed: string;
};

export type EntryCashOutControlsProps = {
    phase: CashOutPhase;
    labels: CashOutLabels;
    offerAmount: string;
    currencyIcon?: ReactNode;
    /** Compact entry-card pills (default) or the larger entry-detail dock buttons with a text Cancel. */
    variant?: CashOutControlsVariant;
    /** Dims and disables Confirm while there is no actionable offer (e.g. the offer is suspended). */
    confirmDisabled?: boolean;
    onCashOut: () => void;
    onCancel: () => void;
    onConfirm: () => void;
};

/** The button region of an open entry, rendered per cash-out phase. Driven entirely by `useEntryCashOut`. */
export const EntryCashOutControls = ({
    phase,
    labels,
    offerAmount,
    currencyIcon,
    variant = 'card',
    confirmDisabled = false,
    onCashOut,
    onCancel,
    onConfirm,
}: EntryCashOutControlsProps) => {
    const { height, borderRadius, textVariant, loaderSize } = CASH_OUT_PILL[variant];
    if (phase === 'idle') {
        return (
            <EntryCardCashOutButton
                label={labels.offer}
                amount={offerAmount}
                leadingIcon={currencyIcon}
                onPress={onCashOut}
                variant={variant}
            />
        );
    }

    if (phase === 'confirm') {
        return (
            <Row gap="s12">
                <Pressable onPress={onCancel} accessibilityRole="button">
                    <CashOutPill height={height} paddingHorizontal="s16">
                        <Text variant={textVariant} color={ds.colorToken.text.primary}>
                            {labels.cancel}
                        </Text>
                    </CashOutPill>
                </Pressable>
                <Pressable
                    onPress={confirmDisabled ? undefined : onConfirm}
                    disabled={confirmDisabled}
                    accessibilityRole="button"
                    accessibilityState={{ disabled: confirmDisabled }}
                    style={common.flex}
                >
                    <CashOutPill
                        height={height}
                        borderRadius={borderRadius}
                        flex={1}
                        backgroundColor={ds.colorToken.background.brand.emphasis}
                        opacity={confirmDisabled ? 0.5 : 1}
                    >
                        <Text variant={textVariant} color={ds.colorToken.text.primary}>
                            {labels.confirm}
                        </Text>
                    </CashOutPill>
                </Pressable>
            </Row>
        );
    }

    if (phase === 'loading') {
        return (
            <CashOutPill
                height={height}
                borderRadius={borderRadius}
                backgroundColor={ds.colorToken.background.brand.emphasis}
                accessibilityState={{ busy: true }}
            >
                <LottieLoader size={loaderSize} color={ds.colorValue.icon.primary} />
            </CashOutPill>
        );
    }

    return (
        <CashOutPill
            height={height}
            borderRadius={borderRadius}
            backgroundColor={ds.colorToken.background.neutral.subtle}
            accessibilityRole="button"
            accessibilityState={{ disabled: true }}
        >
            <Row alignItems="center" gap="s8">
                <CheckmarkSmall color={ds.colorValue.icon.primary} />
                <Text variant={textVariant} color={ds.colorToken.text.primary}>
                    {labels.confirmed}
                </Text>
            </Row>
        </CashOutPill>
    );
};
